import React from "react";
import { ChevronArrowDown, ChevronArrowUp } from "../../../assets/icons";
import { useAppSelector } from "../../../store/hooks/StoreHooks";

interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    type?: string;
    label?: string;
    error?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    value: string | number;
    name?: string;
    placeholder: string;
    disabled?: boolean;
    readOnly?: boolean;
    required?: boolean;
    unit?: string;
    bgColor?: string;
    icon?: { component: React.ReactNode; tooltipText?: string };
    link?: string;
}

const TextInput: React.FC<TextInputProps> = ({
    type = "text",
    label,
    value,
    name,
    placeholder = "Enter text here",
    error,
    onChange,
    disabled = false,
    readOnly = false,
    required = false,
    unit,
    bgColor = "bg-darkcolor-700",
    icon,
    link,
    ...props
}) => {
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);

    const handleIncrease = () => {
        if (type === "number") {
            const currentValue =
                typeof value === "string" ? parseFloat(value) : value;
            const newValue = (isNaN(currentValue) ? 0 : currentValue) + 1;
            const roundedValue = Math.round(newValue * 100) / 100;
            if (onChange) {
                onChange({
                    target: { value: roundedValue.toString(), name },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        }
    };

    const handleDecrease = () => {
        if (type === "number") {
            const currentValue =
                typeof value === "string" ? parseFloat(value) : value;
            const newValue = (isNaN(currentValue) ? 0 : currentValue) - 1;
            const roundedValue = Math.round(newValue * 100) / 100;
            if (onChange) {
                onChange({
                    target: { value: roundedValue.toString(), name },
                } as React.ChangeEvent<HTMLInputElement>);
            }
        }
    };

    return (
        <>
            <div className="flex flex-col w-full gap-[6px] relative">
                {label && (
                    <label className="flex flex-row justify-start items-start text-neutral-25">
                        {label}
                        {required && <span className="text-error-400 ml-1">*</span>}
                        {icon && (
                            <div className="relative inline-block ml-1 group">
                                <a
                                    href={link ?? ""}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {icon.component}
                                </a>
                                <div className="absolute top-0 mb-2 left-[50px] transform -translate-x-1/2 w-max bg-darkcolor-400 text-neutral-25 text-[16px] rounded-[8px] py-1 px-2 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                                    {icon?.tooltipText}
                                </div>
                            </div>
                        )}
                    </label>
                )}
                <div
                    className={`flex items-center w-full rounded-[8px] 
                ${(disabled || (customerEnabled && !customerCreated) || projectReadOnly)
                            ? "w-full bg-transparent text-neutral-400 cursor-not-allowed rounded-[8px] border-[1px] border-neutral-600"
                            : `${bgColor} border-[1px] border-darkcolor-400 hover:border-primary-500`
                        } 
                ${error
                            ? "border-error-500 focus:ring-error-600 focus-within:ring-2 focus-within:ring-error-600"
                            : "focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                        }`}
                >
                    {/* Input Field */}
                    <input
                        className={`flex-grow py-[12px] px-[14px] max-h-[44px] text-neutral-25 focus:outline-none ${(disabled || (customerEnabled && !customerCreated) || projectReadOnly)
                            ? "bg-transparent text-neutral-400 cursor-not-allowed rounded-[8px]"
                            : "bg-transparent"
                            } 
                                ${readOnly
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : ""
                            }`}
                        onChange={onChange}
                        name={name}
                        value={value}
                        placeholder={String(placeholder)}
                        disabled={(customerEnabled && !customerCreated) || projectReadOnly || disabled}
                        readOnly={readOnly}
                        type={type}
                        {...props}
                    />
                    {unit && (
                        <span
                            className="px-3 text-gray-400 whitespace-nowrap"
                            style={{
                                pointerEvents: "none",
                                color: `${(disabled || (customerEnabled && !customerCreated) || projectReadOnly) ? "#5D6675" : "#FFFFFF"}`,
                            }}
                        >
                            {unit}
                        </span>
                    )}
                    {type === "number" && (
                        <span
                            className="flex items-center flex-col justify-center px-[4px] border-l-0 rounded-tr-[8px] rounded-br-[8px]"
                            style={{ userSelect: "none" }}
                        >
                            <ChevronArrowUp
                                size={15}
                                className="relative top-[7px] cursor-pointer"
                                onClick={handleIncrease}
                                color={(!disabled || (!customerEnabled && customerCreated) || !projectReadOnly) && value ? "#F7F9F9" : "gray"}
                            />
                            <ChevronArrowDown
                                size={15}
                                className="relative bottom-[7px] cursor-pointer"
                                onClick={handleDecrease}
                                color={(!disabled || (!customerEnabled && customerCreated) || !projectReadOnly) && value ? "#F7F9F9" : "gray"}
                            />
                        </span>
                    )}
                </div>
                {error && <p className="text-error-500 text-sm">{error}</p>}
            </div>
        </>
    );
};

export default TextInput;
